import { MutationTree } from 'vuex'
import * as consts from '@/utils/constants'
import {
  defaultTemplateData,
  setTemplateVariantToTemplate,
  addTemplateVariant,
  defaultVariantsInfo,
  removeTemplateVariant
} from '@/store/templates/helpers/useTemplatesHelpers'
import { TemplatesStateInterface } from '@/models/store/TemplatesState.interface'
import { FilterAndSearchInterface } from '@/models/templates/FilterAndSearch.interface'
import { TemplateInterface } from '@/models/templates/Template.interface'
import {
  VariantsInfoInterface,
  TemplateVariant
} from '@/models/templates/VariantsInfo.interface'
import { AttachmentInterface } from '@/models/templates/Attachment.interface'
import {
  ContentType,
  RichCardInterface
} from '@/models/templates/RichCard.interface'
import CommonTemplateVariantFields from '@/models/templates/CommonTemplateVariantFields.interface'
import {
  RichCardActionList,
  RichCardActionAt,
  RichCardActionUpdate,
  RichCardActionMove
} from '@/models/rich_cards/RichCardActionPayload.interface'
import {
  emptyRichCard,
  emptyRcsAction,
  richCardAt,
  rcsActionsFor,
  maxFor,
  reindex
} from '@/store/templates/helpers/useRichCardHelpers'

const mutations: MutationTree<TemplatesStateInterface> = {
  loadingTemplates(state: TemplatesStateInterface) {
    state.loading = true
    state.templates = []
  },
  loadedTemplates(
    state: TemplatesStateInterface,
    templates: TemplateInterface[]
  ) {
    state.templates = templates
    state.loading = false
  },
  loadingTemplate(state: TemplatesStateInterface) {
    state.loading = true
    state.template = defaultTemplateData()
  },
  loadedTemplate(state: TemplatesStateInterface, template: TemplateInterface) {
    state.template = template
    state.loading = false
  },
  setFilters(
    state: TemplatesStateInterface,
    filters: FilterAndSearchInterface
  ) {
    state.filterAndSearch = { ...state.filterAndSearch, ...filters }
  },
  updateTemplateState(state: TemplatesStateInterface, data: TemplateInterface) {
    state.template = Object.assign(state.template, data)
  },
  resetTemplateState(state: TemplatesStateInterface, data: TemplateInterface) {
    state.template = {
      ...defaultTemplateData(),
      ...data,
      slug: state.slug!
    }

    if (
      state.template.kind &&
      consts.TEMPLATE_WITHOUT_CONTENT_FIELD_KINDS.includes(state.template.kind)
    )
      state.template.body = ''

    state.loading = false
  },
  setDefaultFromAddress(state: TemplatesStateInterface, fromAddress: string) {
    if (state.template) state.template.fromAddress = fromAddress
  },
  loadedVariantsInfo(
    state: TemplatesStateInterface,
    data: VariantsInfoInterface
  ) {
    state.variantsInfo = {
      ...state.variantsInfo,
      ...data
    }
    state.variantsInfo.loading = false
  },
  setSlug(state: TemplatesStateInterface, slug: string) {
    state.slug = slug
    state.template.slug = slug
  },
  loadingVariantsInfo(state: TemplatesStateInterface) {
    state.variantsInfo.loading = true
  },
  setFallbackTemplateSlug(
    state: TemplatesStateInterface,
    fallbackTemplateSlug: string | null
  ) {
    state.variantsInfo.fallbackTemplateSlug = fallbackTemplateSlug
  },
  addNewTemplateVariant(
    state: TemplatesStateInterface,
    templateVariant: TemplateVariant
  ) {
    addTemplateVariant(state, 'new', templateVariant)
    addTemplateVariant(state, 'available', templateVariant)
  },
  removeTemplateVariant(
    state: TemplatesStateInterface,
    templateVariant: TemplateVariant
  ) {
    removeTemplateVariant(state, 'new', templateVariant)
    removeTemplateVariant(state, 'available', templateVariant)
  },
  destroyTemplateVariants(state: TemplatesStateInterface, slug: string) {
    state.templates = state.templates.filter(
      (template) => template.slug !== slug
    )
  },
  updateCurrentVariant(
    state: TemplatesStateInterface,
    templateVariant: TemplateVariant
  ) {
    state.template = { ...state.template, ...templateVariant }
  },
  setNewCommonTemplateVariantFields(
    state: TemplatesStateInterface,
    data: CommonTemplateVariantFields
  ) {
    state.newCommonTemplateVariantFields = {
      ...state.newCommonTemplateVariantFields,
      ...data
    }
  },
  resetNewCommonTemplateVariantFields(state: TemplatesStateInterface) {
    state.newCommonTemplateVariantFields = {}
  },
  resetVariantsInfo(state: TemplatesStateInterface) {
    state.variantsInfo = { ...defaultVariantsInfo(), loading: false }
    setTemplateVariantToTemplate(state)
  },
  templateChanged(state: TemplatesStateInterface) {
    state.template.changed = true
  },
  resetTemplateChangedFlag(state: TemplatesStateInterface) {
    state.template.changed = false
  },
  markCurrentTemplateVariantPersisted(
    state: TemplatesStateInterface,
    templateVariant: TemplateVariant
  ) {
    removeTemplateVariant(state, 'new', templateVariant)
  },
  updateAttachment(state: TemplatesStateInterface, data: AttachmentInterface) {
    const attachment = state.template.attachmentsMetadata.find(
      (att) => att.id === data.id
    )
    Object.assign(attachment, data)
  },
  addNewAttachment(state: TemplatesStateInterface) {
    let id: number
    if (state.template.attachmentsMetadata.length > 0) {
      const ids = state.template.attachmentsMetadata.map((att) => att.id)
      const sortedIds = ids.sort((a, b) => b - a)
      id = sortedIds[0] + 1
    } else {
      id = 1
    }
    const newAttachment: AttachmentInterface = {
      id,
      type: consts.DEFAULT_ATTACHMENT_TYPE
    }

    state.template.attachmentsMetadata.push(newAttachment)
  },
  removeAttachment(state: TemplatesStateInterface, id: number) {
    state.template.attachmentsMetadata =
      state.template.attachmentsMetadata.filter((att) => att.id !== id)
  },
  addNewHsmParam(state: TemplatesStateInterface) {
    if (!state.template.hsmParams?.length) {
      state.template.hsmParams = []
    }

    state.template.hsmParams.push('')
  },
  removeHsmParam(state: TemplatesStateInterface) {
    if (!state.template.hsmParams?.length) {
      state.template.hsmParams = []
    }

    state.template.hsmParams.pop()
  },
  updateHsmParam(state: TemplatesStateInterface, { data, index }) {
    if (!state.template.hsmParams?.length) {
      state.template.hsmParams = []
    }

    state.template.hsmParams[index] = data
  },
  loadingTemplateSlugs(state: TemplatesStateInterface) {
    state.loadingSlugs = true
    state.templateSlugs = []
  },
  loadedTemplateSlugs(state: TemplatesStateInterface, slugs: string[]) {
    state.templateSlugs = slugs
    state.loadingSlugs = false
  },
  setContentType(state: TemplatesStateInterface, contentType: ContentType) {
    state.template.contentType = contentType
  },
  seedRichCard(state: TemplatesStateInterface) {
    state.template.richCards = [emptyRichCard()]
  },
  updateRichCard(
    state: TemplatesStateInterface,
    data: Partial<RichCardInterface>
  ) {
    const card = richCardAt(state, 0)
    if (card) Object.assign(card, data)
  },
  addRichCardAction(state: TemplatesStateInterface, list: RichCardActionList) {
    const actions = rcsActionsFor(state, list)
    if (!actions || actions.length >= maxFor(list)) return

    actions.push(emptyRcsAction(actions.length))
  },
  updateRichCardAction(
    state: TemplatesStateInterface,
    { list, index, data }: RichCardActionUpdate
  ) {
    const action = rcsActionsFor(state, list)?.[index]
    if (!action) return

    Object.assign(action, data)

    if (data.actionType === consts.RCS_ACTION_OPEN_URL)
      action.phoneNumber = null
    if (data.actionType === consts.RCS_ACTION_DIAL_PHONE) action.url = null
  },
  removeRichCardAction(
    state: TemplatesStateInterface,
    { list, index }: RichCardActionAt
  ) {
    const actions = rcsActionsFor(state, list)
    if (!actions) return

    actions.splice(index, 1)
    reindex(actions)
  },
  moveRichCardAction(
    state: TemplatesStateInterface,
    { list, index, offset }: RichCardActionMove
  ) {
    const actions = rcsActionsFor(state, list)
    const target = index + offset
    if (!actions || target < 0 || target >= actions.length) return

    const [action] = actions.splice(index, 1)
    actions.splice(target, 0, action)
    reindex(actions)
  }
}

export default mutations
